import { Fragment, useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SearchSelectField } from '@/components/ui/SearchSelectField/SearchSelectField';
import { api } from '@/api/client';
import type { PersonSearchResult, ProjectReader } from '@/api/client';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { Badge } from '@/components/ui/Badge/Badge';
import { groupReadersForPickerSections } from '@/utils/pickerProgressGroups';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import '@/components/ui/SectionDividerWithLines/SectionDividerWithLines.css';
import '@/components/ui/SearchSelectField/SearchSelectField.css';
import './ReaderCombo.css';

const PAGE_SIZE = 25;
const MIN_SEARCH_LENGTH = 2;
const LOCAL_DEBOUNCE_MS = 300;

export type ReaderValueItem = { id: string; name: string };

interface ReaderComboProps {
  value: ReaderValueItem[];
  onChange: (readers: ReaderValueItem[]) => void;
  placeholder?: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  /** Одиночный выбор (единственный сценарий в приложении) */
  single?: boolean;
  /** Множественный выбор для подтипов сессий, где один слот занимает несколько чтецов. */
  multiple?: boolean;
  /** Только эти чтецы (например, состав проекта). Поиск по подстроке без API */
  readersFilter?: { id: string; name: string }[];
  onReaderSelect?: (reader: PersonSearchResult) => void;
}

type ReaderRow = ReaderValueItem;

function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: () => void,
  options?: { excludeSelector?: string }
) {
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (options?.excludeSelector && (target as Element).closest?.(options.excludeSelector)) return;
      handler();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [ref, handler, options?.excludeSelector]);
}

/** Список чтецов проекта: группы «Завершён / В процессе / В очереди» и прежняя сортировка внутри группы. */
function ReaderComboProjectReadersList({
  value,
  onChange,
  placeholder,
  disabled,
  containerRef,
  readersFilter,
  multiple = false,
  onReaderSelect,
}: {
  value: ReaderValueItem[];
  onChange: (readers: ReaderValueItem[]) => void;
  placeholder: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  readersFilter: ProjectReader[];
  multiple?: boolean;
  onReaderSelect?: (reader: PersonSearchResult) => void;
}) {
  const pickedReader: ReaderValueItem | null = (value[0] as ReaderValueItem | undefined) ?? null;
  const selectedIds = useMemo(() => new Set(value.map((r) => r.id)), [value]);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const innerRef = useRef<HTMLDivElement>(null);
  const dropdownWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLElement | null>(null);
  const scrollToHighlightRef = useRef(false);
  const skipClearInputOnNullRef = useRef(false);
  const caretAfterCommitEditRef = useRef<number | null>(null);
  const resolvedContainerRef = containerRef ?? innerRef;
  const dropdownStyle = useDropdownPosition(resolvedContainerRef, open, { dropdownRef: dropdownWrapRef });

  const debouncedSearch = useDebouncedValue(inputValue.trim(), LOCAL_DEBOUNCE_MS);

  const matched = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return readersFilter.filter((r) => !q || r.name.toLowerCase().includes(q));
  }, [readersFilter, debouncedSearch]);

  const sections = useMemo(() => groupReadersForPickerSections(matched), [matched]);
  const flatOptions = useMemo(() => sections.flatMap((s) => s.readers), [sections]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [open, flatOptions.length, debouncedSearch]);

  useEffect(() => {
    if (!open) return;
    if (!scrollToHighlightRef.current) return;
    scrollToHighlightRef.current = false;
    const el = listRef.current?.querySelector(`[data-option-index="${highlightIndex}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  useLayoutEffect(() => {
    const pos = caretAfterCommitEditRef.current;
    if (pos == null) return;
    caretAfterCommitEditRef.current = null;
    const el = inputRef.current;
    if (!el) return;
    const clamped = Math.min(Math.max(0, pos), el.value.length);
    el.setSelectionRange(clamped, clamped);
  }, [pickedReader, inputValue]);

  useEffect(() => {
    if (pickedReader !== null) return;
    if (skipClearInputOnNullRef.current) {
      skipClearInputOnNullRef.current = false;
      return;
    }
    setInputValue('');
  }, [pickedReader]);

  useClickOutside(resolvedContainerRef, () => setOpen(false), {
    excludeSelector: '[data-combo-dropdown]',
  });

  const selectRow = useCallback(
    (row: ProjectReader) => {
      if (onReaderSelect) onReaderSelect(row);
      if (multiple) {
        const exists = selectedIds.has(row.id);
        onChange(exists ? value.filter((r) => r.id !== row.id) : [...value, { id: row.id, name: row.name }]);
      } else {
        onChange([{ id: row.id, name: row.name }]);
      }
      setInputValue('');
      if (!multiple) setOpen(false);
    },
    [multiple, onChange, onReaderSelect, selectedIds, value]
  );

  const removeReader = useCallback(
    (reader: ReaderValueItem) => {
      onChange(value.filter((r) => r.id !== reader.id));
    },
    [onChange, value]
  );

  const searchQuery = inputValue.trim();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (pickedReader && !multiple) {
      const label = pickedReader.name;
      const el = e.currentTarget;
      const rawStart = el.selectionStart;
      const rawEnd = el.selectionEnd;
      const start = rawStart ?? label.length;
      const end = rawEnd ?? label.length;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        let next: string;
        let caret: number;
        if (start !== end) {
          next = label.slice(0, start) + label.slice(end);
          caret = start;
        } else if (e.key === 'Backspace') {
          if (start === 0) return;
          next = label.slice(0, start - 1) + label.slice(start);
          caret = start - 1;
        } else {
          if (start >= label.length) return;
          next = label.slice(0, start) + label.slice(start + 1);
          caret = start;
        }
        skipClearInputOnNullRef.current = true;
        onChange([]);
        setInputValue(next);
        caretAfterCommitEditRef.current = Math.min(caret, next.length);
        setOpen(next.trim().length > 0 || readersFilter.length > 0);
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        const next = label.slice(0, start) + e.key + label.slice(end);
        skipClearInputOnNullRef.current = true;
        onChange([]);
        setInputValue(next);
        caretAfterCommitEditRef.current = Math.min(start + 1, next.length);
        setOpen(next.trim().length > 0 || readersFilter.length > 0);
        return;
      }
      return;
    }

    if (!open) {
      const canOpenKeyboard = searchQuery.length > 0 || readersFilter.length > 0;
      if ((e.key === 'ArrowDown' || e.key === 'Enter') && canOpenKeyboard) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      scrollToHighlightRef.current = true;
      setHighlightIndex((i) => Math.min(i + 1, Math.max(0, flatOptions.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      scrollToHighlightRef.current = true;
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flatOptions[highlightIndex];
      if (row) selectRow(row);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const hasValue = !!pickedReader;
  const hasMultipleValue = multiple && value.length > 0;
  const displayValue = multiple ? inputValue : hasValue ? pickedReader.name : inputValue;
  const showDropdown =
    !disabled && (multiple || !hasValue) && open && (inputValue.trim().length > 0 || readersFilter.length > 0);

  return (
    <div ref={innerRef} className="search-select-field">
      <div
        className={`search-select-field__field reader-combo__field ${multiple ? 'reader-combo__field_multiple' : ''} ${
          (hasValue || hasMultipleValue) && !disabled ? 'search-select-field__field_has-clear' : ''
        }`}
      >
        {multiple && value.length > 0 && (
          <div className="reader-combo__chips">
            {value.map((reader) => (
              <Badge key={reader.id} variant="reader">
                {reader.name}
                {!disabled && (
                  <button
                    type="button"
                    className="badge__remove"
                    onClick={() => removeReader(reader)}
                    aria-label={`Удалить ${reader.name}`}
                  >
                    ×
                  </button>
                )}
              </Badge>
            ))}
          </div>
        )}
        <div className="search-select-field__input-wrap">
          <input
            ref={inputRef}
            className={`search-select-field__input ${!multiple && hasValue ? 'search-select-field__input_readonly' : ''}`}
            value={displayValue}
            readOnly={!multiple && hasValue}
            onChange={(e) => {
              if (disabled || (!multiple && hasValue)) return;
              const v = e.target.value;
              setInputValue(v);
              setOpen(true);
            }}
            onFocus={() => {
              if (!disabled && (multiple || !hasValue)) setOpen(true);
            }}
            onPaste={(e) => {
              if (disabled || multiple || !pickedReader) return;
              e.preventDefault();
              const text = e.clipboardData.getData('text/plain');
              const label = pickedReader.name;
              const el = e.currentTarget;
              const start = el.selectionStart ?? label.length;
              const end = el.selectionEnd ?? label.length;
              const next = label.slice(0, start) + text + label.slice(end);
              skipClearInputOnNullRef.current = true;
              onChange([]);
              setInputValue(next);
              caretAfterCommitEditRef.current = Math.min(start + text.length, next.length);
              setOpen(next.trim().length > 0 || readersFilter.length > 0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={disabled || (multiple && value.length > 0) ? '' : placeholder}
            disabled={disabled}
            autoComplete="off"
            aria-expanded={open}
            aria-haspopup="listbox"
          />
        </div>
        {(hasValue || hasMultipleValue) && !disabled && (
          <ComboClearButton
            onClick={() => {
              onChange([]);
              setInputValue('');
              setOpen(false);
            }}
          />
        )}
      </div>
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownWrapRef}
            className="search-select-field__dropdown-wrap"
            data-combo-dropdown
            style={dropdownStyle}
          >
            {flatOptions.length === 0 ? (
              <div className="search-select-field__hint">Нет результатов</div>
            ) : (
              <OverlayScrollArea
                contentRef={listRef}
                element="ul"
                className="search-select-field__dropdown"
                role="listbox"
              >
                {(() => {
                  let optionIdx = 0;
                  return sections.map((sec) => (
                    <Fragment key={sec.label}>
                      <li role="presentation" className="reader-combo__section-divider">
                        <div className="section-divider-with-lines section-divider-with-lines_full-bleed">
                          <span className="section-divider-with-lines__label">{sec.label}</span>
                        </div>
                      </li>
                      {sec.readers.map((r) => {
                        const i = optionIdx++;
                        return (
                          <li
                            key={r.id}
                            role="option"
                            data-option-index={i}
                            aria-selected={selectedIds.has(r.id)}
                            className={`search-select-field__option ${i === highlightIndex ? 'search-select-field__option_highlight' : ''}`}
                            onMouseDown={(ev) => {
                              ev.preventDefault();
                              selectRow(r);
                            }}
                            onMouseEnter={() => setHighlightIndex(i)}
                          >
                            {r.name}
                          </li>
                        );
                      })}
                    </Fragment>
                  ));
                })()}
              </OverlayScrollArea>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export function ReaderCombo({
  value,
  onChange,
  placeholder = 'Чтец',
  disabled,
  containerRef,
  readersFilter,
  multiple = false,
  onReaderSelect,
}: ReaderComboProps) {
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();
  const selected: ReaderRow | null = value[0] ?? null;
  const valueRef = useRef(value);
  valueRef.current = value;

  const filterKey = useMemo(
    () => (readersFilter?.length ? readersFilter.map((r) => r.id).sort().join(',') : ''),
    [readersFilter]
  );

  const handleChange = (row: ReaderRow | null) => {
    if (row && onReaderSelect) {
      onReaderSelect(row);
    }
    onChange(row ? [{ id: row.id, name: row.name }] : []);
  };

  if (multiple || readersFilter?.length) {
    return (
      <ReaderComboProjectReadersList
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        containerRef={containerRef}
        readersFilter={(readersFilter ?? []) as ProjectReader[]}
        multiple={multiple}
        onReaderSelect={onReaderSelect}
      />
    );
  }

  return (
    <SearchSelectField<ReaderRow>
      value={selected}
      onChange={handleChange}
      getOptionLabel={(r) => r.name}
      placeholder={placeholder}
      disabled={disabled}
      containerRef={containerRef}
      queryKey={['persons', 'search-field', filterKey]}
      allowEmptySearch={false}
      minSearchLength={MIN_SEARCH_LENGTH}
      fetchPage={async (debouncedQuery, page) => {
        const res = await api.persons.list(debouncedQuery, {
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        });
        return res;
      }}
      computeAddons={({ debouncedQuery, filteredItems }) => {
        if (!canManageCatalog) return [];
        const q = debouncedQuery.trim();
        if (!q || filteredItems.length > 0) return [];
        return [
          {
            id: '__create_reader',
            label: 'Создать чтеца',
            onSelect: () => {
              openCatalogManage({
                tab: 'persons',
                rolePreset: 'reader',
                nameSeed: q,
                onPersonCreated: (payload) => {
                  const current = valueRef.current;
                  const ids = new Set(current.map((r) => r.id));
                  const next = [...current];
                  for (const r of payload.readers) {
                    if (!ids.has(r.id)) {
                      next.push({ id: r.id, name: r.name });
                      ids.add(r.id);
                    }
                  }
                  if (payload.readers.length === 0 && payload.person && !ids.has(payload.person.id)) {
                    next.push({
                      id: payload.person.id,
                      name: payload.person.name,
                    });
                  }
                  if (next.length !== current.length) onChange(next);
                },
              });
            },
          },
        ];
      }}
    />
  );
}
